import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { listArticles } from "@/blog/articles";
import { CATEGORIES, listCategories } from "@/blog/categories";
import { SITE_URL, SITE_NAME } from "@/lib/seo";
import { useLanguage } from "@/lib/i18n";

/**
 * Blog index page. Renders identically for CA and ES — the `lang` prop drives
 * which version of each article (title/excerpt/slug) is displayed.
 */
export default function BlogList({ lang = "ca" }) {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("all");

  const articles = useMemo(() => {
    const all = listArticles();
    return activeCategory === "all"
      ? all
      : all.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  const langPrefix = lang === "es" ? "/es" : "";
  const blogBase = `${langPrefix}/blog`;

  const heading = lang === "es"
    ? "Recursos para empezar a moverte"
    : "Recursos per començar a moure't";
  const subheading = lang === "es"
    ? "Guías prácticas, sin juicios y basadas en evidencia para recuperar capacidad funcional, movilidad y confianza — paso a paso."
    : "Guies pràctiques, sense judicis i basades en evidència per recuperar capacitat funcional, mobilitat i confiança — pas a pas.";

  // ItemList schema for the blog index (helps Google understand the list)
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${SITE_NAME} Blog`,
    url: `${SITE_URL}${blogBase}`,
    inLanguage: lang === "es" ? "es-ES" : "ca-ES",
    blogPost: articles.map((a) => {
      const v = a[lang];
      return {
        "@type": "BlogPosting",
        headline: v.title,
        url: `${SITE_URL}${blogBase}/${v.slug}`,
        datePublished: a.publishedAt,
        dateModified: a.updatedAt,
        inLanguage: lang === "es" ? "es-ES" : "ca-ES",
      };
    }),
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="blog-list-page">
      <SEO
        title={lang === "es" ? "Blog · Recursos para empezar a moverte" : "Blog · Recursos per començar a moure't"}
        description={subheading}
        path={blogBase}
        lang={lang}
        alternates={[
          { lang: "ca", path: "/blog" },
          { lang: "es", path: "/es/blog" },
        ]}
        schema={schema}
      />

      <Navbar />

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <Breadcrumbs
          items={[{ label: lang === "es" ? "Blog" : "Blog" }]}
          homeLabel={lang === "es" ? "Inicio" : "Inici"}
        />

        <header className="mt-8 mb-12 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display mb-4">
            {lang === "es" ? "Blog · AI METABOLIC" : "Blog · AI METABOLIC"}
          </p>
          <h1 className="font-sans-display text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] tracking-tight leading-tight mb-4">
            {heading}
          </h1>
          <p className="text-lg text-[#475569] leading-relaxed">{subheading}</p>
        </header>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filters">
          <CategoryChip
            active={activeCategory === "all"}
            onClick={() => setActiveCategory("all")}
            label={lang === "es" ? "Todos" : "Tots"}
            testid="filter-all"
          />
          {listCategories().map((cat) => (
            <CategoryChip
              key={cat.key}
              active={activeCategory === cat.key}
              onClick={() => setActiveCategory(cat.key)}
              label={cat.label[lang]}
              testid={`filter-${cat.key}`}
            />
          ))}
        </div>

        {articles.length === 0 ? (
          <p className="text-[#475569] py-12 text-center" data-testid="blog-empty">
            {lang === "es" ? "Aún no hay artículos en esta categoría." : "Encara no hi ha articles en aquesta categoria."}
          </p>
        ) : (
          <div className="space-y-8">
            {/* Pillar article gets a full-width featured card */}
            {articles.filter((a) => a.isPillar).map((a) => (
              <PillarCard key={a.id} article={a} lang={lang} blogBase={blogBase} />
            ))}

            {/* Standard cluster articles in a 2-col grid */}
            <div className="grid gap-6 sm:grid-cols-2">
              {articles.filter((a) => !a.isPillar).map((a) => (
                <ArticleCard key={a.id} article={a} lang={lang} blogBase={blogBase} />
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function CategoryChip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 text-sm font-sans-display rounded-full transition ${
        active
          ? "bg-[#0B2545] text-[#F9F8F6]"
          : "bg-white border border-slate-200 text-[#475569] hover:border-[#0B2545]/40 hover:text-[#0B2545]"
      }`}
    >
      {label}
    </button>
  );
}

function ArticleCard({ article, lang, blogBase }) {
  const v = article[lang];
  const category = CATEGORIES[article.category];
  return (
    <Link
      to={`${blogBase}/${v.slug}`}
      data-testid={`article-card-${article.id}`}
      className="group block bg-white border border-slate-200 rounded-xl p-6 sm:p-7 hover:border-[#0B2545]/30 hover:shadow-sm transition"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#B4C5A4] font-sans-display font-medium">
          {category ? category.label[lang] : ""}
        </span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
          <Clock className="h-3 w-3" /> {article.readingMinutes} min
        </span>
      </div>
      <h2 className="font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug mb-3 group-hover:text-[#0a1f3a]">
        {v.title}
      </h2>
      <p className="text-sm text-[#475569] leading-relaxed mb-5">{v.excerpt}</p>
      <span className="inline-flex items-center gap-1 text-xs font-sans-display tracking-wide text-[#0B2545] group-hover:gap-2 transition-all">
        {lang === "es" ? "Leer artículo" : "Llegir article"}
        <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}

function PillarCard({ article, lang, blogBase }) {
  const v = article[lang];
  return (
    <Link
      to={`${blogBase}/${v.slug}`}
      data-testid={`pillar-card-${article.id}`}
      className="group block bg-[#0B2545] text-[#F9F8F6] border border-[#0B2545] rounded-2xl p-8 sm:p-10 hover:shadow-xl transition relative overflow-hidden"
    >
      <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold">
        {lang === "es" ? "★ Guía principal" : "★ Guia principal"}
      </span>
      <div className="flex items-center gap-3 mb-4 text-[#B4C5A4]">
        <span className="text-[10px] uppercase tracking-[0.18em] font-sans-display font-medium">
          {lang === "es" ? "Activación" : "Activació"}
        </span>
        <span className="opacity-50">·</span>
        <span className="inline-flex items-center gap-1 text-xs">
          <Clock className="h-3 w-3" /> {article.readingMinutes} min
        </span>
      </div>
      <h2 className="font-sans-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight mb-4 max-w-3xl">
        {v.title}
      </h2>
      <p className="text-[#F9F8F6]/80 leading-relaxed mb-6 max-w-2xl">{v.excerpt}</p>
      <span className="inline-flex items-center gap-1.5 text-sm font-sans-display tracking-wide text-[#B4C5A4] group-hover:gap-2.5 transition-all">
        {lang === "es" ? "Leer la guía completa" : "Llegir la guia completa"}
        <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  );
}
